import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Video, Star, Calendar, BookOpen } from "lucide-react";

export default function MasteryWorkshop() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-purple-600 to-blue-700 text-white py-12 overflow-hidden">
        {/* Removed SVG Pattern Overlay */}
        {/*
        <svg className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="pattern-circles" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <circle cx="20" cy="20" r="3" fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#pattern-circles)" />
        </svg>
        */}
        {/* Updated container width to 1400px */}
        <div className="relative container mx-auto px-4 max-w-6xl z-10 text-center">
          <Badge
            variant="secondary"
            className="mb-3 bg-white/10 backdrop-blur-sm"
          >
            2025 Course - Now Available
          </Badge>
          <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">
            Master Google Ads Automation: <br /> Scripts & Sheets Mastery 2025
          </h1>
          <p className="text-2xl mb-6 text-white/90 drop-shadow">
            Learn Google Ads Scripts, MCC Scripts & Google Apps Scripts with AI.
            <br />
            The first course to include MCC Scripts training!
          </p>
          <div className="flex justify-center gap-6 flex-wrap mb-6">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Video className="h-5 w-5" />
              <span className="font-semibold">
                9 Modules, 3+ Hours of Training
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Star className="h-5 w-5" />
              <span className="font-semibold">
                MCC Scripts & Apps Script Included
              </span>
            </div>
          </div>
          <a
            href="https://mikerhodes.circle.so/checkout/ssm2025"
            className="inline-block"
          >
            <Button
              size="lg"
              className="bg-white text-purple-600 hover:bg-white/90 transition-colors duration-200 shadow-lg"
            >
              Get Instant Access for Only €249
            </Button>
          </a>
        </div>
      </div>

      {/* Main Content */}
      {/* Updated container width to 1400px */}
      <div className="container mx-auto px-8 py-16 max-w-6xl">
        <div className="grid md:grid-cols-2 gap-10 mb-16">
          {/* Left Column - Course Modules */}
          <Card className="transition-shadow duration-300 hover:shadow-2xl rounded-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-6 w-6 text-purple-600" />
                Course Modules - 9 Modules, 3+ Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 1: Script Basics (16 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    1-1 Introduction To Google Ads Scripts And Automation (9
                    min)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    1-2 Running Scripts And Working With Logs (7 min)
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 2: Custom Script Development (14 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    2-1 Customizing Scripts And Using Cursor Editor (14 min)
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 3: Script Structure (21 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    3-1 Understanding Script Structure And Components (21 min)
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 4: AI-Powered Script Writing (42 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    4-1 Writing Scripts With AI Basic Prompting (11 min)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    4-2 Using API Documentation With AI (7 min)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    4-3 Comparing AI Models For Script Writing (10 min)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    4-4 Debugging Scripts And Error Handling (24 min)
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 5: Google Sheets Integration (44 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    5-1 Google Sheets Formulas With AI Dynamic Data (18 min)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    5-2 Array Formulas And Conditional Formatting (12 min)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    5-3 Import Range And Data Transfer (14 min)
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 6: MCC Scripts (23 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    6-1 MCC Scripts Single Tab Reporting Pattern (11 min)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    6-2 MCC Scripts Multiple Tabs And Master Sheets (12 min)
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 7: Apps Script Automation (13 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    7-1 Google Apps Script Gmail Automation And Knowledge Base
                    (13 min)
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 8: Advanced AI Techniques (10 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    8-1 The Mega Prompt Advanced AI Code Generation (10 min)
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-purple-600 mb-1">
                    Module 9: Course Resources (5 min)
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    9-1 Cloning The Code Repository Getting All Scripts (5 min)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - What's Included */}
          <Card className="transition-shadow duration-300 hover:shadow-2xl rounded-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-purple-600" />
                What's Included
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-3">
                    Core Training Materials
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        9 comprehensive video modules
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Over 3 hours of step-by-step training
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Complete code repository access
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Downloadable resources and templates
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">
                    New 2025 Features
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        MCC Scripts training (new for 2025)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Google Apps Script automation (also new)
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        AI-powered script writing techniques
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Advanced debugging and error handling
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Bonus Content</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        The Mega Prompt for AI code generation
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        AI model comparison guide
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Import Range and data transfer techniques
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Array formulas and conditional formatting
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">
                    Lifetime Benefits
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Lifetime access to all content
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Free updates and new content
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Private community access
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-500 mt-1">✓</span>
                      <span className="text-muted-foreground">
                        Priority support
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Bonus Section */}
        <Card className="mb-16 transition-shadow duration-300 hover:shadow-2xl rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-center justify-center">
              <Star className="h-6 w-6 text-green-600" />
              Additional Bonus - Limited Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <div className="inline-block bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg px-6 py-4">
                <h3 className="text-2xl font-bold text-green-700 dark:text-green-400 mb-2">
                  FREE: Negative Keyword Script
                </h3>
                <p className="text-green-600 dark:text-green-300 font-semibold">
                  Value: $109 • Included at no extra cost
                </p>
              </div>

              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Get my premium Negative Keyword Script that automates the work
                needed to discover new negative keywords. This powerful script
                analyzes your search terms data and identifies optimization
                opportunities automatically.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mt-6">
                <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4 border border-green-200 dark:border-green-700">
                  <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">
                    Automated Discovery
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Automatically finds negative keyword opportunities from your
                    search terms data
                  </p>
                </div>

                <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4 border border-green-200 dark:border-green-700">
                  <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">
                    Time Saving
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Eliminates hours of manual search term analysis and negative
                    keyword research
                  </p>
                </div>

                <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-4 border border-green-200 dark:border-green-700">
                  <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">
                    Proven Results
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Used by hundreds of advertisers to improve campaign
                    performance and reduce wasted spend
                  </p>
                </div>
              </div>

              <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-4 mt-6">
                <p className="text-green-800 dark:text-green-200 font-medium">
                  🎁 The time saved with this bonus script will pay back the
                  cost of the course. Get both the comprehensive training AND a
                  valuable automation tool that saves you hours every month.
                  Plus you'll learn the skills to edit the code & make it
                  exactly the way YOU want it.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Final CTA Section */}
        <div className="text-center space-y-6 py-8">
          <h2 className="text-3xl font-bold">
            Ready to Revolutionize Your Campaigns?
          </h2>
          <p className="text-xl text-muted-foreground">
            Take control of your automation & career.
            <br />
            Unlock the complete workshop recordings and start automating success
            today!
          </p>
          <div className="flex flex-col items-center gap-4">
            <div className="text-4xl font-bold text-purple-600">€249</div>
            <a
              href="https://mikerhodes.circle.so/checkout/ssm2025"
              className="inline-block"
            >
              <Button
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 transition-colors duration-200 shadow-lg"
              >
                Claim Instant Access Now
              </Button>
            </a>
            <p className="text-sm text-muted-foreground">
              One‑time payment • Price in EUR • Lifetime access with free
              updates • Free for Ads to AI members
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
